/**
 * ALOHANOVA UNIVERSE - Cosmic Wallpaper Background
 * Copyright © 2025 Alohanova Universe, Malcolm KH Lee III. All Rights Reserved.
 * 
 * Seamless Looping Cosmic Background
 */

import React, { useState, useEffect } from 'react';

const CosmicWallpaperBackground = ({ autoPlay = true, transitionSpeed = 10000 }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // High-quality cosmic wallpapers
  const cosmicImages = [
    {
      url: 'https://images.unsplash.com/photo-1610209455607-89e8b3e0e393?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxjb3NtaWMlMjBnYWxheHl8ZW58MHx8fHB1cnBsZXwxNzYxNDU0Mjk2fDA&ixlib=rb-4.1.0&q=85',
      name: 'Purple Spiral Galaxy'
    },
    {
      url: 'https://images.unsplash.com/photo-1601074741256-26ae877a41a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxjb3NtaWMlMjBnYWxheHl8ZW58MHx8fHB1cnBsZXwxNzYxNDU0Mjk2fDA&ixlib=rb-4.1.0&q=85',
      name: 'Purple Starry Sky'
    },
    {
      url: 'https://images.unsplash.com/photo-1602979082099-7971376fce79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxuZWJ1bGElMjBzcGFjZXxlbnwwfHx8cHVycGxlfDE3NjE0NTQzMTN8MA&ixlib=rb-4.1.0&q=85',
      name: 'Purple Nebula Texture'
    }
  ];

  useEffect(() => {
    // Preload all images
    cosmicImages.forEach(img => {
      const image = new Image();
      image.src = img.url;
    });
    setIsLoaded(true);

    // Auto-rotate images
    if (autoPlay) {
      const interval = setInterval(() => {
        setCurrentImage(prev => (prev + 1) % cosmicImages.length);
      }, transitionSpeed);

      return () => clearInterval(interval);
    }
  }, [autoPlay, transitionSpeed]);

  return (
    <div className="fixed inset-0 -z-10 overflow-hidden">
      {/* Background Images with Crossfade */}
      {cosmicImages.map((img, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-2000 ease-in-out ${
            index === currentImage ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: `url(${img.url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'brightness(0.7) contrast(1.1)', // Enhance cosmic feel
          }}
        />
      ))}

      {/* Overlay for depth and atmosphere */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/20 to-gray-900/60" />
      
      {/* Animated cosmic dust particles */}
      <div className="absolute inset-0 opacity-30">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Cosmic glow effect */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'radial-gradient(circle at 30% 50%, rgba(139, 92, 246, 0.3) 0%, transparent 50%)',
        }}
      />
    </div>
  );
};

export default CosmicWallpaperBackground;
